import { FEATURE_NOT_ENABLED, PLAN_INACTIVE } from '../billing/billing-error-codes.js';
import {
  COVER_SHIPMENTS_ATTACHMENT_MATERIAL_MISMATCH,
  COVER_SHIPMENTS_ATTACHMENT_UNAVAILABLE,
  COVER_SHIPMENTS_INVENTORY_POLICY_BLOCKED,
  COVER_SHIPMENTS_LINES_ALREADY_USED,
  COVER_SHIPMENTS_NOT_FOUND,
  COVER_SHIPMENTS_PERMISSION_DENIED,
  COVER_SHIPMENTS_VALIDATION_FAILED,
} from '../cover-shipments/cover-shipments-error-codes.js';
import {
  HOLDING_UNIT_RESERVED,
  HOLDING_UNIT_SCANNABLE_IDENTIFIER_DUPLICATE,
  HOLDING_UNIT_VERSION_CONFLICT,
  RESERVATION_CONFLICT,
} from '../holding-units/holding-units-error-codes.js';
import { PURCHASES_SYNC_DUPLICATE_FOLIO } from '../purchases/purchases-error-codes.js';
import { PERMISSION_DENIED } from '../roles/roles-error-codes.js';
import {
  SHIPMENTS_LINES_ALREADY_SHIPPED,
  SHIPMENTS_RC_SYNC_GATE_FAILED,
  SHIPMENTS_VOID_REQUIRES_REVERSAL,
} from '../shipments/shipments-error-codes.js';
import { TENANCY_COMPANY_SUSPENDED } from '../tenancy/tenancy-error-codes.js';
import { z } from 'zod';

import {
  INTERNAL_SERVER_ERROR,
  LEGACY_VERSION_CONFLICT_RECHECK_REQUIRED,
  OUTBOX_ACKED_PREDECESSOR_CLOUD_BEHIND,
  OUTBOX_DEPENDENCIES_UNRESOLVED,
  OUTBOX_PAYLOAD_UNSUPPORTED,
  OUTBOX_STREAM_LOCAL_GAP,
  SYNC_ACTOR_INVALID,
  SYNC_CLIENT_ID_REQUIRED,
  SYNC_COMPANY_MISMATCH,
  SYNC_DEPENDENCY_MISSING,
  SYNC_DUPLICATE_EVENT_MISMATCH,
  SYNC_INVALID_REQUEST,
  SYNC_RESULT_PROTOCOL_INVALID,
  SYNC_TRANSACTION_CONFLICT,
  SYNC_STATION_BINDING_INVALID,
  SYNC_STATION_RECOVERED_ON_ANOTHER_DEVICE,
  SYNC_UNSUPPORTED_EVENT_TYPE,
} from './sync-error-codes.js';

export const syncDeliveryFailureClassValues = [
  'RETRYABLE',
  'DEPENDENCY',
  'CONFIGURATION',
  'INVALID',
  'DIVERGENCE',
  'INTEGRITY',
] as const;
export type SyncDeliveryFailureClass = (typeof syncDeliveryFailureClassValues)[number];

export const holdingUnitSyncConflictEntityTypeValues = [
  'holdingUnit',
  'loadSession',
  'fixedIdAsset',
] as const;
export type HoldingUnitSyncConflictEntityType =
  (typeof holdingUnitSyncConflictEntityTypeValues)[number];

export interface SyncHoldingUnitVersionConflict {
  entityType: HoldingUnitSyncConflictEntityType;
  entityId: string;
  cloudHeadVersion: number;
  incomingFirstVersion: number;
  incomingLastVersion: number;
  incomingEventIds: string[];
}

export interface SyncHoldingUnitVersionConflictDetails {
  kind: 'holding_unit_version_conflict';
  conflicts: SyncHoldingUnitVersionConflict[];
}

export interface SyncActorInvalidDetails {
  invalidActorUserIds: string[];
}

export interface SyncFeatureNotEnabledDetails {
  requiredFeatureKey: string;
}

export type SyncDeliveryErrorDetails =
  | SyncHoldingUnitVersionConflictDetails
  | SyncActorInvalidDetails
  | SyncFeatureNotEnabledDetails;

export const SyncHoldingUnitVersionConflictSchema: z.ZodType<SyncHoldingUnitVersionConflict> = z
  .object({
    entityType: z.enum(holdingUnitSyncConflictEntityTypeValues),
    entityId: z.uuid(),
    cloudHeadVersion: z.number().int().nonnegative(),
    incomingFirstVersion: z.number().int().positive(),
    incomingLastVersion: z.number().int().positive(),
    incomingEventIds: z.array(z.uuid()).min(1),
  })
  .strict()
  .refine((value) => value.incomingLastVersion >= value.incomingFirstVersion, {
    message: 'incomingLastVersion must be greater than or equal to incomingFirstVersion',
    path: ['incomingLastVersion'],
  });

export const SyncHoldingUnitVersionConflictDetailsSchema: z.ZodType<SyncHoldingUnitVersionConflictDetails> =
  z
    .object({
      kind: z.literal('holding_unit_version_conflict'),
      conflicts: z.array(SyncHoldingUnitVersionConflictSchema).min(1),
    })
    .strict();

export const SyncActorInvalidDetailsSchema: z.ZodType<SyncActorInvalidDetails> = z
  .object({
    invalidActorUserIds: z.array(z.uuid()).min(1).max(250),
  })
  .strict();

export const SyncFeatureNotEnabledDetailsSchema: z.ZodType<SyncFeatureNotEnabledDetails> = z
  .object({
    requiredFeatureKey: z.string().trim().min(1).max(128),
  })
  .strict();

export const SyncDeliveryErrorDetailsSchema: z.ZodType<SyncDeliveryErrorDetails> = z.union([
  SyncHoldingUnitVersionConflictDetailsSchema,
  SyncActorInvalidDetailsSchema,
  SyncFeatureNotEnabledDetailsSchema,
]);

export const syncDeliveryErrorCodeValues = [
  SYNC_DEPENDENCY_MISSING,
  SHIPMENTS_RC_SYNC_GATE_FAILED,
  INTERNAL_SERVER_ERROR,
  PERMISSION_DENIED,
  FEATURE_NOT_ENABLED,
  PLAN_INACTIVE,
  TENANCY_COMPANY_SUSPENDED,
  SYNC_UNSUPPORTED_EVENT_TYPE,
  SYNC_ACTOR_INVALID,
  SYNC_STATION_BINDING_INVALID,
  SYNC_STATION_RECOVERED_ON_ANOTHER_DEVICE,
  HOLDING_UNIT_VERSION_CONFLICT,
  SYNC_INVALID_REQUEST,
  SYNC_COMPANY_MISMATCH,
  SYNC_CLIENT_ID_REQUIRED,
  SYNC_DUPLICATE_EVENT_MISMATCH,
  PURCHASES_SYNC_DUPLICATE_FOLIO,
  HOLDING_UNIT_RESERVED,
  HOLDING_UNIT_SCANNABLE_IDENTIFIER_DUPLICATE,
  RESERVATION_CONFLICT,
  SHIPMENTS_LINES_ALREADY_SHIPPED,
  SHIPMENTS_VOID_REQUIRES_REVERSAL,
  COVER_SHIPMENTS_ATTACHMENT_UNAVAILABLE,
  COVER_SHIPMENTS_ATTACHMENT_MATERIAL_MISMATCH,
  COVER_SHIPMENTS_LINES_ALREADY_USED,
  COVER_SHIPMENTS_INVENTORY_POLICY_BLOCKED,
  COVER_SHIPMENTS_PERMISSION_DENIED,
  COVER_SHIPMENTS_VALIDATION_FAILED,
  COVER_SHIPMENTS_NOT_FOUND,
  SYNC_RESULT_PROTOCOL_INVALID,
  SYNC_TRANSACTION_CONFLICT,
  OUTBOX_DEPENDENCIES_UNRESOLVED,
  OUTBOX_STREAM_LOCAL_GAP,
  OUTBOX_ACKED_PREDECESSOR_CLOUD_BEHIND,
  OUTBOX_PAYLOAD_UNSUPPORTED,
  LEGACY_VERSION_CONFLICT_RECHECK_REQUIRED,
] as const;

export type SyncDeliveryErrorCode = (typeof syncDeliveryErrorCodeValues)[number];

interface SyncDeliveryErrorDefinition {
  errorClass: SyncDeliveryFailureClass;
  detailsSchema: z.ZodType;
}

const NoDetailsSchema = z.undefined();

export const syncDeliveryErrorRegistry = {
  [SYNC_DEPENDENCY_MISSING]: { errorClass: 'RETRYABLE', detailsSchema: NoDetailsSchema },
  [SHIPMENTS_RC_SYNC_GATE_FAILED]: { errorClass: 'RETRYABLE', detailsSchema: NoDetailsSchema },
  [INTERNAL_SERVER_ERROR]: { errorClass: 'RETRYABLE', detailsSchema: NoDetailsSchema },
  [PERMISSION_DENIED]: { errorClass: 'CONFIGURATION', detailsSchema: NoDetailsSchema },
  [FEATURE_NOT_ENABLED]: {
    errorClass: 'CONFIGURATION',
    detailsSchema: SyncFeatureNotEnabledDetailsSchema,
  },
  [PLAN_INACTIVE]: { errorClass: 'CONFIGURATION', detailsSchema: NoDetailsSchema },
  [TENANCY_COMPANY_SUSPENDED]: { errorClass: 'CONFIGURATION', detailsSchema: NoDetailsSchema },
  [SYNC_UNSUPPORTED_EVENT_TYPE]: { errorClass: 'CONFIGURATION', detailsSchema: NoDetailsSchema },
  [SYNC_ACTOR_INVALID]: {
    errorClass: 'CONFIGURATION',
    detailsSchema: SyncActorInvalidDetailsSchema,
  },
  [SYNC_STATION_BINDING_INVALID]: {
    errorClass: 'CONFIGURATION',
    detailsSchema: NoDetailsSchema,
  },
  [SYNC_STATION_RECOVERED_ON_ANOTHER_DEVICE]: {
    errorClass: 'CONFIGURATION',
    detailsSchema: NoDetailsSchema,
  },
  [HOLDING_UNIT_VERSION_CONFLICT]: {
    errorClass: 'DEPENDENCY',
    detailsSchema: SyncHoldingUnitVersionConflictDetailsSchema,
  },
  [SYNC_INVALID_REQUEST]: { errorClass: 'INVALID', detailsSchema: NoDetailsSchema },
  [SYNC_COMPANY_MISMATCH]: { errorClass: 'INVALID', detailsSchema: NoDetailsSchema },
  [SYNC_CLIENT_ID_REQUIRED]: { errorClass: 'INVALID', detailsSchema: NoDetailsSchema },
  [SYNC_DUPLICATE_EVENT_MISMATCH]: { errorClass: 'INTEGRITY', detailsSchema: NoDetailsSchema },
  [PURCHASES_SYNC_DUPLICATE_FOLIO]: { errorClass: 'INVALID', detailsSchema: NoDetailsSchema },
  [HOLDING_UNIT_RESERVED]: { errorClass: 'INVALID', detailsSchema: NoDetailsSchema },
  [HOLDING_UNIT_SCANNABLE_IDENTIFIER_DUPLICATE]: {
    errorClass: 'INVALID',
    detailsSchema: NoDetailsSchema,
  },
  [RESERVATION_CONFLICT]: { errorClass: 'INVALID', detailsSchema: NoDetailsSchema },
  [SHIPMENTS_LINES_ALREADY_SHIPPED]: { errorClass: 'INVALID', detailsSchema: NoDetailsSchema },
  [SHIPMENTS_VOID_REQUIRES_REVERSAL]: { errorClass: 'INVALID', detailsSchema: NoDetailsSchema },
  [COVER_SHIPMENTS_ATTACHMENT_UNAVAILABLE]: {
    errorClass: 'INVALID',
    detailsSchema: NoDetailsSchema,
  },
  [COVER_SHIPMENTS_ATTACHMENT_MATERIAL_MISMATCH]: {
    errorClass: 'INVALID',
    detailsSchema: NoDetailsSchema,
  },
  [COVER_SHIPMENTS_LINES_ALREADY_USED]: { errorClass: 'INVALID', detailsSchema: NoDetailsSchema },
  [COVER_SHIPMENTS_INVENTORY_POLICY_BLOCKED]: {
    errorClass: 'INVALID',
    detailsSchema: NoDetailsSchema,
  },
  [COVER_SHIPMENTS_PERMISSION_DENIED]: { errorClass: 'INVALID', detailsSchema: NoDetailsSchema },
  [COVER_SHIPMENTS_VALIDATION_FAILED]: { errorClass: 'INVALID', detailsSchema: NoDetailsSchema },
  [COVER_SHIPMENTS_NOT_FOUND]: { errorClass: 'INVALID', detailsSchema: NoDetailsSchema },
  [SYNC_RESULT_PROTOCOL_INVALID]: { errorClass: 'INTEGRITY', detailsSchema: NoDetailsSchema },
  [SYNC_TRANSACTION_CONFLICT]: { errorClass: 'RETRYABLE', detailsSchema: NoDetailsSchema },
  [OUTBOX_DEPENDENCIES_UNRESOLVED]: { errorClass: 'INTEGRITY', detailsSchema: NoDetailsSchema },
  [OUTBOX_STREAM_LOCAL_GAP]: { errorClass: 'INTEGRITY', detailsSchema: NoDetailsSchema },
  [OUTBOX_ACKED_PREDECESSOR_CLOUD_BEHIND]: {
    errorClass: 'INTEGRITY',
    detailsSchema: NoDetailsSchema,
  },
  [OUTBOX_PAYLOAD_UNSUPPORTED]: { errorClass: 'INVALID', detailsSchema: NoDetailsSchema },
  [LEGACY_VERSION_CONFLICT_RECHECK_REQUIRED]: {
    errorClass: 'CONFIGURATION',
    detailsSchema: NoDetailsSchema,
  },
} satisfies Record<SyncDeliveryErrorCode, SyncDeliveryErrorDefinition>;

export const SyncDeliveryErrorCodeSchema: z.ZodType<SyncDeliveryErrorCode> = z.enum(
  syncDeliveryErrorCodeValues,
);

export function isSyncDeliveryErrorCode(value: string): value is SyncDeliveryErrorCode {
  return SyncDeliveryErrorCodeSchema.safeParse(value).success;
}

export function getSyncDeliveryFailureClass(
  errorCode: SyncDeliveryErrorCode,
): SyncDeliveryFailureClass {
  return syncDeliveryErrorRegistry[errorCode].errorClass;
}

export function isValidSyncDeliveryErrorDetails(
  errorCode: SyncDeliveryErrorCode,
  details: unknown,
): boolean {
  return syncDeliveryErrorRegistry[errorCode].detailsSchema.safeParse(details).success;
}
